import { Button, DateValue, RangeValue, TmdxDateRangePicker } from '@ds';
import { MdOutlineClear } from 'react-icons/md';
import { ConditionalRender, FilterDropdown } from '@ui';
import { DONOR_TYPES } from '@features/components/CaseFormBlocks/config';
import {
  ArchiveCasesFiltersState,
  ArchiveFilterConfig,
  FILTERS_NAMES,
  INVOICE_STATE_SELECT_LIST,
  INVOICE_STATUS_SELECT_LIST,
  OUTCOME_SELECT_LIST,
  OUTCOME_STATUS_SELECT_LIST,
  RANDOMIZATION_FILTER_SELECT_LIST,
  STATUS_SELECT_LIST,
} from '@features/components/Archive/config';
import { CaseTypeFilterDropdown } from '@features/components/CaseFilters/CaseTypeFilterDropdown';
import { OPOFilterDropdown } from '@features/components/CaseFilters/OPOFilterDropdown';
import { ORGAN_SELECT_LIST } from '@/constants/form.constants';
import { ArchiveDateFiltersName, CaseStatus, DonorType, OrganType, OutcomeType, InvoiceState } from '@/api/apis/case';
import { useAsyncSearchableList } from '@/hooks/useAsyncSearchableList';
import { useTrialProgramsList } from '@/hooks/useTrialProgramsList';
import { useInfiniteTXCentersListQuery } from '@/queries/tx-centers';
import { NameByValue } from '@/types';
import { createDateRangeFromArrays, formatCalendarDateTime } from '@/utils/helpers';
import { serviceRegionItems } from '@/constants/filters';

type ArchiveCasesFiltersProps = {
  filterConfig?: ArchiveFilterConfig;
  filters: ArchiveCasesFiltersState;
  hasFilters: boolean;
  updateFilters: (
    filter: ArchiveCasesFiltersState[keyof ArchiveCasesFiltersState],
    type: keyof ArchiveCasesFiltersState,
  ) => void;
  clearFilters: () => void;
};

export const ArchiveCasesFilters = ({
  filterConfig,
  filters,
  hasFilters,
  updateFilters,
  clearFilters,
}: ArchiveCasesFiltersProps) => {
  const {
    list: txCenterList,
    isLoading,
    hasNextPage,
    loadNextPage,
    setSearchValue: onLocationSearch,
  } = useAsyncSearchableList({
    query: useInfiniteTXCentersListQuery,
    defaultSortOrder: { orderBy: 'name', asc: true },
  });

  const { trialProgramsList } = useTrialProgramsList();

  const dateRanges = {
    Service: createDateRangeFromArrays(filters.serviceRequestDateTimeFrom, filters.serviceRequestDateTimeTo),
    Outcome: createDateRangeFromArrays(filters.outcomeDateTimeFrom, filters.outcomeDateTimeTo),
    OffOCS: createDateRangeFromArrays(filters.offOCSDateTimeFrom, filters.offOCSDateTimeTo),
  };

  const handleDateChange = (range: RangeValue<DateValue> | null, type: ArchiveDateFiltersName) => {
    const startDateValue = range?.start ? [range.start] : [];
    const endDateValue = range?.end ? [range.end] : [];

    const startDateTime = startDateValue.length ? (formatCalendarDateTime(startDateValue[0], true) ?? '') : '';
    const endDateTime = endDateValue.length ? (formatCalendarDateTime(endDateValue[0], false) ?? '') : '';

    switch (type) {
      case ArchiveDateFiltersName.Service:
        updateFilters(startDateTime, 'serviceRequestDateTimeFrom');
        updateFilters(endDateTime, 'serviceRequestDateTimeTo');
        break;
      case ArchiveDateFiltersName.Outcome:
        updateFilters(startDateTime, 'outcomeDateTimeFrom');
        updateFilters(endDateTime, 'outcomeDateTimeTo');
        break;
      case ArchiveDateFiltersName.OffOCS:
        updateFilters(startDateTime, 'offOCSDateTimeFrom');
        updateFilters(endDateTime, 'offOCSDateTimeTo');
        break;
      default:
        break;
    }
  };

  const handleDateReset = (type: ArchiveDateFiltersName) => {
    handleDateChange(null, type);
  };

  return (
    <div className="flex gap-7 items-center flex-wrap">
      <FilterDropdown
        title={FILTERS_NAMES.organType}
        items={ORGAN_SELECT_LIST}
        selectedKeys={filters.organType}
        mode="multiple"
        onSelectedKeysChange={(values) => updateFilters(values as OrganType[], 'organType')}
        showResetButton
      />
      <CaseTypeFilterDropdown
        opoRequest={!!filters.opoRequest}
        isReallocation={!!filters.isReallocatedFromSource}
        onFiltersChange={(nextOpoRequest, nextIsReallocation) => {
          updateFilters(nextOpoRequest || undefined, 'opoRequest');
          updateFilters(nextIsReallocation || undefined, 'isReallocatedFromSource');
        }}
        showResetButton
      />
      <FilterDropdown
        title={FILTERS_NAMES.donorType}
        items={DONOR_TYPES}
        selectedKeys={filters.donorType}
        mode="multiple"
        onSelectedKeysChange={(values) => updateFilters(values as DonorType[], 'donorType')}
        showResetButton
      />
      <FilterDropdown
        title={FILTERS_NAMES.caseStatuses}
        items={STATUS_SELECT_LIST}
        selectedKeys={filters.caseStatuses}
        mode="multiple"
        onSelectedKeysChange={(values) => updateFilters(values as CaseStatus[], 'caseStatuses')}
        showResetButton
      />
      <FilterDropdown
        title={FILTERS_NAMES.outcomeType}
        items={filterConfig?.outcomeList ?? OUTCOME_SELECT_LIST}
        selectedKeys={filters.outcomeType}
        mode="multiple"
        onSelectedKeysChange={(values) => updateFilters(values as OutcomeType[], 'outcomeType')}
        showResetButton
      />
      <FilterDropdown
        title={FILTERS_NAMES.caseOutcome}
        items={OUTCOME_STATUS_SELECT_LIST}
        selectedKeys={filters.caseOutcome}
        mode="multiple"
        onSelectedKeysChange={(values) => updateFilters(values as CaseStatus[], 'caseOutcome')}
        showResetButton
      />
      <FilterDropdown
        title="TX Center"
        items={txCenterList as NameByValue<string>[]}
        selectedKeys={filters.txCenterIds}
        mode="multiple"
        hasNextPage={hasNextPage}
        isLoading={isLoading}
        loadNextPage={loadNextPage}
        onSearchValue={onLocationSearch}
        onSelectedKeysChange={(values) => updateFilters(values as string[], 'txCenterIds')}
        showResetButton
      />
      <OPOFilterDropdown
        selectedKeys={filters.opoIds}
        onSelectedKeysChange={(values) => updateFilters(values, 'opoIds')}
        showResetButton
      />
      <FilterDropdown
        title={FILTERS_NAMES.serviceDates}
        customContent={
          <TmdxDateRangePicker
            size="lg"
            color="primary"
            value={dateRanges.Service}
            label="Between"
            onChange={(val) => handleDateChange(val, ArchiveDateFiltersName.Service)}
            aria-label="Filter by service date range"
          />
        }
        hasCustomValue={!!dateRanges.Service}
        customValueCount={Number(!!dateRanges.Service)}
        onReset={() => handleDateReset(ArchiveDateFiltersName.Service)}
        showResetButton
        showCounter
      />
      <FilterDropdown
        title={FILTERS_NAMES.outcomeDates}
        customContent={
          <TmdxDateRangePicker
            size="lg"
            color="primary"
            value={dateRanges.Outcome}
            label="Between"
            onChange={(val) => handleDateChange(val, ArchiveDateFiltersName.Outcome)}
            aria-label="Filter by outcome date range"
          />
        }
        hasCustomValue={!!dateRanges.Outcome}
        customValueCount={Number(!!dateRanges.Outcome)}
        onReset={() => handleDateReset(ArchiveDateFiltersName.Outcome)}
        showResetButton
        showCounter
      />
      <FilterDropdown
        title={FILTERS_NAMES.offOCSDates}
        customContent={
          <TmdxDateRangePicker
            size="lg"
            color="primary"
            value={dateRanges.OffOCS}
            label="Between"
            onChange={(val) => handleDateChange(val, ArchiveDateFiltersName.OffOCS)}
            aria-label="Filter by off ocs/scs date range"
          />
        }
        hasCustomValue={!!dateRanges.OffOCS}
        customValueCount={Number(!!dateRanges.OffOCS)}
        onReset={() => handleDateReset(ArchiveDateFiltersName.OffOCS)}
        showResetButton
        showCounter
      />
      <FilterDropdown
        title={FILTERS_NAMES.hasInvoice}
        items={INVOICE_STATE_SELECT_LIST}
        selectedKeys={filters.hasInvoice}
        mode="multiple"
        onSelectedKeysChange={(values) => updateFilters(values as InvoiceState[], 'hasInvoice')}
        showResetButton
      />
      <FilterDropdown
        title={FILTERS_NAMES.invoiceStatuses}
        items={INVOICE_STATUS_SELECT_LIST}
        selectedKeys={filters.invoiceStatuses}
        mode="multiple"
        onSelectedKeysChange={(values) =>
          updateFilters(values as ArchiveCasesFiltersState['invoiceStatuses'], 'invoiceStatuses')
        }
        showResetButton
      />
      <FilterDropdown
        title={FILTERS_NAMES.trialProgramIds}
        items={trialProgramsList}
        selectedKeys={filters.trialProgramIds}
        mode="multiple"
        onSelectedKeysChange={(values) => updateFilters(values as string[], 'trialProgramIds')}
        showResetButton
      />
      <FilterDropdown
        title={FILTERS_NAMES.randomizationResultFilter}
        items={RANDOMIZATION_FILTER_SELECT_LIST}
        selectedKeys={filters.randomizationResultFilter}
        mode="multiple"
        onSelectedKeysChange={(values) =>
          updateFilters(values as ArchiveCasesFiltersState['randomizationResultFilter'], 'randomizationResultFilter')
        }
        showResetButton
      />
      <FilterDropdown
        title={FILTERS_NAMES.serviceRegionIds}
        items={serviceRegionItems}
        selectedKeys={filters.serviceRegionIds}
        mode="multiple"
        onSelectedKeysChange={(values) =>
          updateFilters(values as ArchiveCasesFiltersState['serviceRegionIds'], 'serviceRegionIds')
        }
        showResetButton
      />

      <ConditionalRender show={hasFilters}>
        <Button startContent={<MdOutlineClear size={18} />} size="sm" variant="outline" onClick={clearFilters}>
          Clear all
        </Button>
      </ConditionalRender>
    </div>
  );
};
